import * as React from "react";
import { useContext, useEffect, useState } from "react";
import {
  Stack,
  Heading,
  Text,
  TextLink,
  List,
  ListItem,
  Button,
} from "@origin-digital/ods-core";
import FormFieldLayout from "../FormComponents/FormFieldLayout";
import { Field, useFormikContext, FieldProps, yupToFormErrors } from "formik";
import DatePickerField from "../FormComponents/DatePickerField";
import { DocumentTypeEnum, ErrorTypeEnum } from "../types";
import { removeSupplyElectricityValidationSchema } from "./validationSchema";
import RemoveSupplyContext from "src/context/RemoveSupplyContext";
import { apiClient } from "src/api/apiService";
import { getErrorTypeEnum, normaliseDate } from "src/api/apiHelper";
import { useNavigate } from "react-router-dom";
import { isUnavailableDateForFuture } from "../helper";
import { getPayerDetails } from "./helpers";
import { AppContext } from "src/context/AppContext";
import { RemoveSupplyFormValues } from "./types";
import { RemoveSupplyFormFileUploadFormikField } from "./RemoveSupplyFormFileUploadFormikField";

const RemoveSupplyServiceBlock: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { values, errors, setErrors } =
    useFormikContext<RemoveSupplyFormValues>();
  const {
    setDisplayingAlertBanner,
    setDisplayingSnackbar,
    draftInfo,
    setDraftInfo,
  } = useContext(RemoveSupplyContext);
  const navigate = useNavigate();
  const {
    context: { user },
  } = useContext(AppContext);

  const state = values.premise?.site_details?.state ?? "";
  const energyType = values.energyType;
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      //Check all the fields within the whole form. If any field has an error, display the error banner.
      setDisplayingAlertBanner(ErrorTypeEnum.OCP_FIELD_VALIDATION);
    } else {
      setDisplayingAlertBanner(null);
    }
  }, [errors]);

  const saveDraft = async () => {
    try {
      setErrors({});
      setIsLoading(true);
      const { data: draftData } = await apiClient.post("/draft/premise", {
        premise: values.premise,
        services: [
          {
            draft_id: draftInfo?.draft_id,
            draft_version: draftInfo?.draft_version,
            request_id: draftInfo?.request_id,
            service_type: energyType === "electricity" ? "ELECTRICITY" : "GAS ",
            request_type: "REMOVE_SUPPLY",
            payload: {
              scheduledDate: normaliseDate(values.abolishmentDate, null),
              payerDetails: getPayerDetails({
                noCompany: !!values.noCompany,
                user: user!,
              }),
              requestorType: "OTHER",
            },
            nmi_mirn: energyType === "electricity" ? values.nmi : values.mirn,
          },
        ],
      });
      setDraftInfo({
        draft_id: draftData.services[0].draft_id,
        draft_version: draftData.services[0].draft_version,
        request_id: draftData.services[0].request_id,
        raw_request_status: draftData.services[0].raw_request_status,
        request_type: draftData.services[0].request_type,
        message_enabled: draftData.services[0].message_enabled,
      });
      setDisplayingSnackbar({
        message: `Draft has been saved.`,
        tone: "positive",
      });
    } catch (e: any) {
      setDisplayingSnackbar({
        message: `Couldn't save draft`,
        tone: "caution",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const submitRequest = async () => {
    setDisplayingSnackbar(null);
    setIsLoading(true);
    try {
      const response = await apiClient.put(
        `/submission/${draftInfo?.request_id}`
      );

      setDisplayingAlertBanner(null);

      navigate("/changes", {
        state: {
          submitted: true,
          journeyId: response.data.services[0].journey_id,
          snackbarMessage: "Request has been submitted.",
        },
      });
    } catch (e: any) {
      setDisplayingAlertBanner(getErrorTypeEnum(e) ?? null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack
      space={{
        xs: "xlarge",
        md: "xxlarge",
      }}
    >
      <Stack
        space={{
          xs: "xlarge",
          md: "xxlarge",
        }}
        id="removeDetailsSection"
      >
        <FormFieldLayout>
          <Heading variant="h4">Removal details</Heading>
        </FormFieldLayout>

        <FormFieldLayout text="When we engage our customer we will only consider the date that they have provided.">
          <DatePickerField
            name="abolishmentDate"
            label="Proposed date of removal of supply"
            shouldDisableDate={isUnavailableDateForFuture}
          />
        </FormFieldLayout>

        {/* VESI form is only required for electricity in Victoria */}
        {state === "VIC" && energyType === "electricity" && (
          <FormFieldLayout
            multipleFields
            text={
              <>
                Upload a completed{" "}
                <TextLink
                  href="https://www.originenergy.com.au/content/dam/origin/residential/docs/new-connections/Abolishment%20form_VIC.pdf"
                  target="_blank"
                >
                  VESI form
                </TextLink>{" "}
                for your distributor
              </>
            }
          >
            <Text weight="medium">VESI form</Text>
            <Field name="abolishmentForm">
              {({ field }: FieldProps) => (
                <RemoveSupplyFormFileUploadFormikField
                  draftId={draftInfo?.draft_id ?? ""}
                  documentTypes={[DocumentTypeEnum.VESI_ABOLISHMENT_FORM]}
                  field={field}
                />
              )}
            </Field>
          </FormFieldLayout>
        )}
      </Stack>

      <Stack
        space={{
          xs: "xlarge",
          md: "xxlarge",
        }}
        id="agreementSection"
      >
        <FormFieldLayout multipleFields>
          <Heading variant="h4">Agreement</Heading>
          <Text>By clicking Lodge Request, you understand and agree that:</Text>
          <List>
            <ListItem>
              <Text>
                Upon submitting this form, Origin will engage directly with our
                account holder and will only process a supply abolishment on a
                verified request from our customer.
              </Text>
            </ListItem>
            <ListItem>
              <Text>
                Distributors and metering providers may charge fees for removing
                their meters and/or supply and they'll pass on any fees directly
                to us. If they charge us we'll pass the fees on to our account
                holder.
              </Text>
            </ListItem>
            <ListItem>
              <Text>
                By submitting this form, Origin will directly communicate with
                our account holder and will only process a removal request upon
                receiving the verified request from our customer.
              </Text>
            </ListItem>
          </List>
        </FormFieldLayout>
      </Stack>

      {/* Actions */}
      <Button
        spinner={isLoading}
        onClick={() => {
          removeSupplyElectricityValidationSchema
            .validate(values, {
              abortEarly: false,
              context: {
                state,
                energyType,
              },
            })
            .then(saveDraft)
            .then(submitRequest)
            .catch((e) => {
              setErrors(yupToFormErrors(e));
            });
        }}
      >
        Lodge request
      </Button>
    </Stack>
  );
};

export default RemoveSupplyServiceBlock;
